import React, { useState, useEffect, useRef } from 'react';
import { format, parse, set, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, parseISO, startOfWeek, getDay, addDays, endOfWeek } from 'date-fns';
import './AddReservationModal.css';
import { supabase } from '../../utils/supabase/client';
import { useAuth } from '../../api/AuthProvider';
import { useRestaurant } from '../../api/restaurant';
import { sendEmail } from '../../api/emails';

const CustomDatePicker = ({ selectedDate, onDateSelect, onClose }) => {
  const currentDate = selectedDate ? parseISO(selectedDate) : new Date();
  const [viewDate, setViewDate] = useState(currentDate);

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const firstDayOfMonth = getDay(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = addDays(endOfWeek(monthEnd, { weekStartsOn: 1 }), 6);
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });

  const monthNames = [
    'Јануари', 'Февруари', 'Март', 'Април', 'Мај', 'Јуни',
    'Јули', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'
  ];

  const dayNames = ['Пон', 'Вто', 'Сре', 'Чет', 'Пет', 'Саб', 'Нед'];

  const handleDateClick = (date) => {
    onDateSelect(format(date, 'yyyy-MM-dd'));
    onClose();
  };

  const goToPreviousMonth = () => {
    setViewDate(subMonths(viewDate, 1));
  };

  const goToNextMonth = () => {
    setViewDate(addMonths(viewDate, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setViewDate(today);
    onDateSelect(format(today, 'yyyy-MM-dd'));
    onClose();
  };

  const adjustedCalendarDays = [];
  let currentWeek = [];
  calendarDays.forEach((date, index) => {
    currentWeek.push(date);
    if ((index + 1) % 7 === 0 || index === calendarDays.length - 1) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      adjustedCalendarDays.push(currentWeek);
      currentWeek = [];
    }
  });

  return (
    <div className="arm-custom-datepicker-overlay" onClick={onClose}>
      <div className="arm-custom-datepicker" onClick={(e) => e.stopPropagation()}>
        <div className="arm-datepicker-header">
          <button className="arm-month-nav" onClick={goToPreviousMonth}>‹</button>
          <span className="arm-month-year">
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
          </span>
          <button className="arm-month-nav" onClick={goToNextMonth}>›</button>
        </div>

        <div className="arm-datepicker-days-header">
          {dayNames.map((day) => (
            <div key={day} className="arm-day-header">{day}</div>
          ))}
        </div>

        <div className="arm-datepicker-days">
          {adjustedCalendarDays.map((week, weekIndex) => (
            <div key={weekIndex} className="arm-calendar-week">
              {week.map((date, dayIndex) => {
                if (!date) {
                  return <div key={`empty-${weekIndex}-${dayIndex}`} className="arm-day-cell arm-empty"></div>;
                }
                const isSelected = selectedDate && isSameDay(date, parseISO(selectedDate));
                const isToday = isSameDay(date, new Date());
                const isCurrentMonth = isSameMonth(date, viewDate);

                return (
                  <button
                    key={date.toString()}
                    className={`arm-day-cell ${isSelected ? 'arm-selected' : ''} ${isToday ? 'arm-today' : ''} ${!isCurrentMonth ? 'arm-other-month' : ''}`}
                    onClick={() => handleDateClick(date)}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="arm-datepicker-footer">
          <button className="arm-today-btn" onClick={goToToday}>Денес</button>
          <button className="arm-close-btn" onClick={onClose}>Затвори</button>
        </div>
      </div>
    </div>
  );
};

const CustomTimePicker = ({ value, onChange, onClose }) => {
  const { data, isLoading } = useRestaurant();

  const reservationFrom = typeof data?.reservationFrom === 'number' ? Math.floor(data.reservationFrom) : 0;
  const reservationTo = typeof data?.reservationTo === 'number' ? Math.floor(data.reservationTo) : 23;

  const fromHour = Math.min(reservationFrom, reservationTo);
  const toHour = Math.max(reservationFrom, reservationTo);

  const initialHour = value ? parseInt(value.split(':')[0], 10) : fromHour;
  const initialMinute = value ? value.split(':')[1] : '00';
  const validInitialHour = Math.max(fromHour, Math.min(toHour, initialHour)).toString().padStart(2, '0');
  const validInitialMinute = ['00', '15', '30', '45'].includes(initialMinute) ? initialMinute : '00';

  const [tempHour, setTempHour] = useState(validInitialHour);
  const [tempMinute, setTempMinute] = useState(validInitialMinute);

  const hours = Array.from(
    { length: toHour - fromHour + 1 },
    (_, i) => (fromHour + i).toString().padStart(2, '0')
  );

  const minutes = ['00', '15', '30', '45'];

  const handleConfirm = () => {
    onChange(`${tempHour}:${tempMinute}`);
    onClose();
  };

  if (isLoading) {
    return <div>Loading restaurant data...</div>;
  }

  return (
    <div className="arm-custom-timepicker-overlay" onClick={onClose}>
      <div className="arm-custom-timepicker" onClick={(e) => e.stopPropagation()}>
        <div className="arm-timepicker-header">
          <h3>Избери време</h3>
        </div>

        <div className="arm-time-display">
          <span className="arm-time-value">{tempHour}:{tempMinute}</span>
        </div>

        <div className="arm-time-selectors">
          <div className="arm-time-column">
            <label>Час</label>
            <div className="arm-time-scroll">
              {hours.map((hour) => (
                <button
                  key={hour}
                  className={`arm-time-option ${tempHour === hour ? 'arm-selected' : ''}`}
                  onClick={() => setTempHour(hour)}
                >
                  {hour}
                </button>
              ))}
            </div>
          </div>

          <div className="arm-time-separator">:</div>

          <div className="arm-time-column">
            <label>Минути</label>
            <div className="arm-time-scroll">
              {minutes.map((minute) => (
                <button
                  key={minute}
                  className={`arm-time-option ${tempMinute === minute ? 'arm-selected' : ''}`}
                  onClick={() => setTempMinute(minute)}
                >
                  {minute}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="arm-timepicker-footer">
          <button className="arm-cancel-btn" onClick={onClose}>Откажи</button>
          <button className="arm-confirm-btn" onClick={handleConfirm}>Потврди</button>
        </div>
      </div>
    </div>
  );
};

const AddReservationModal = ({ isOpen, onClose, onSubmit, selectedDate, isWalkIn = false }) => {
  const [name, setName] = useState('');
  const [partySize, setPartySize] = useState(1);
  const { data: restaurantData, isLoading: isRestaurantLoading } = useRestaurant();
  
  const initialHour = restaurantData?.reservationFrom
    ? Math.floor(restaurantData.reservationFrom).toString().padStart(2, '0')
    : '00';
  const initialMinute = '00';
  const [time, setTime] = useState(`${initialHour}:${initialMinute}`);
  const [date, setDate] = useState(selectedDate);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [additionalRequest, setAdditionalRequest] = useState('');
  const [language, setLanguage] = useState('MK');
  const [error, setError] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const { profile } = useAuth();
  const modalContentRef = useRef(null);
  const [modalHeight, setModalHeight] = useState('auto');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const updateModalHeight = () => {
      if (window.visualViewport) {
        setModalHeight(`${window.visualViewport.height}px`);
      } else {
        setModalHeight(`${window.innerHeight}px`);
      }
    };

    const handleResize = () => {
      updateModalHeight();
    };

    updateModalHeight();
    window.visualViewport?.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleInputFocus = (e) => {
    setTimeout(() => {
      if (modalContentRef.current) {
        const input = e.target;
        const rect = input.getBoundingClientRect();
        const modalRect = modalContentRef.current.getBoundingClientRect();
        if (rect.bottom > window.visualViewport?.height || rect.bottom > window.innerHeight) {
          modalContentRef.current.scrollTo({
            top: input.offsetTop - modalRect.top - 20,
            behavior: 'smooth',
          });
        }
      }
    }, 300);
  };

  const convertDateToShow = (date) => { 
    if (!date) {
      return "Внеси Датум";
    }
    date = new Date(date);
    let dateString = `${date.getDate().toString().padStart(2,'0')}/${(date.getMonth()+1).toString().padStart(2,'0')}/${date.getFullYear()}`;
    return dateString; 
  };

  const convertTimeToShow = (time) => {
    if (!time) return "Избери време";
    const [hour, minute] = time.split(':');
    return `${hour}:${minute}`;
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate that at least one of phone or email is provided for non-walk-in reservations
    if (!isWalkIn && (!phone && !email)) {
      setError('Ве молиме внесете барем телефонски број или email адреса.');
      return;
    }

    // Optional: Validate email format if provided
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Ве молиме внесете валидна email адреса.');
      return;
    }

    // Optional: Validate phone format if provided (e.g., Macedonian phone number format)
    if (phone && !/^\+389\s?7[0-8]\s?\d{3}\s?\d{3}$/.test(phone)) {
      setError('Ве молиме внесете валиден телефонски број (на пр. +389 70 123 456).');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      let guestId;
      let existingCustomer = null;

      if (phone || email) {
        let query = supabase
          .from('customers')
          .select('id, reservations')
          .eq('restaurant_id', profile.restaurantid);

        if (phone) query = query.eq('phone', phone);
        if (email) query = query.eq('email', email);
        if (name) query = query.eq('name', name);

        const { data: customerData, error: customerError } = await query.single();

        if (customerError && customerError.code !== 'PGRST116') {
          throw new Error('Грешка при проверка на клиент: ' + customerError.message);
        }

        existingCustomer = customerData;
      }

      if (existingCustomer) {
        guestId = existingCustomer.id;
        const { error: updateError } = await supabase
          .from('customers')
          .update({ reservations: existingCustomer.reservations + 1 })
          .eq('id', guestId);

        if (updateError) {
          throw new Error('Грешка при ажурирање на резервациите на клиентот: ' + updateError.message);
        }
      } else {
        const { data: newCustomer, error: createError } = await supabase
          .from('customers')
          .insert([
            {
              phone: phone || null,
              email: email || null,
              name: name || 'Гостин',
              restaurant_id: profile.restaurantid,
              reservations: 1,
            },
          ])
          .select('id')
          .single();

        if (createError) {
          throw new Error('Грешка при креирање на клиент: ' + createError.message);
        }
        guestId = newCustomer.id;
      }

      const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
      const [hours, minutes] = time.split(':').map(Number);
      const reservationDateTime = set(parsedDate, { hours, minutes, seconds: 0, milliseconds: 0 });

      const reservation = {
        guest_id: guestId,
        name: name || 'Гостин',
        partySize: parseInt(partySize),
        time: reservationDateTime.toISOString(),
        restaurantid: profile.restaurantid,
        tableIds: [],
        language: language,
        additionalRequest: additionalRequest
      };

      const { error: reservationError } = await supabase
        .from('reservations')
        .insert([reservation]);

      if (reservationError) {
        throw new Error('Грешка при креирање на резервација: ' + reservationError.message);
      }

      if (email && !isWalkIn) {
        const placeholders = {
          'Guest Name': name || 'Guest',
          'Restaurant Name': restaurantData?.name || 'Our Restaurant',
          'Reservation Date': convertDateToShow(date),
          'Reservation Time': convertTimeToShow(time),
          'Number of Guests': partySize,
          'Restaurant Address': restaurantData?.location || 'Unknown Address',
          'Contact Info': restaurantData?.contactInfo || 'contact@freetable.mk',
        };

        const sendResult = await sendEmail({
          restaurantId: profile.restaurantid,
          templateKey: 'confirmed',
          language: language,
          recipient: email,
          placeholders,
        });

        if (!sendResult.success) {
          console.warn('Failed to send confirmation email:', sendResult.error);
          setError(`Резервацијата е креирана, но грешка при испраќање на потврдниот имейл: ${sendResult.error}`);
        }
      }

      onSubmit({
        name: name || 'Гостин',
        partySize: parseInt(partySize),
        time: reservationDateTime.toISOString(),
        phone: phone || null,
        email: email || null,
        tableIds: [],
        guestId
      });

      setName('');
      setPartySize(1);
      setTime(`${initialHour}:${initialMinute}`);
      setDate(selectedDate);
      setPhone('');
      setEmail('');
      setAdditionalRequest('');
      setLanguage('MK');
      setError('');
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="arm-modal-overlay" style={{ height: modalHeight }}>
        <div className="arm-modal-content" ref={modalContentRef}>
          <h2>{isWalkIn ? 'Додади влез без резервација' : 'Додади резервација'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="arm-form-group">
              <label htmlFor="name">Име и презиме</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onFocus={handleInputFocus}
                required={!isWalkIn}
                placeholder={isWalkIn ? 'Опционално' : ''}
              />
            </div>
            <div className="arm-form-group">
              <label htmlFor="partySize">Број на луѓе</label>
              <input
                type="number"
                id="partySize"
                value={partySize}
                onChange={(e) => setPartySize(e.target.value)}
                onFocus={handleInputFocus}
                min="1"
                required
              />
            </div>
            <div className="arm-form-group">
              <label htmlFor="date">Датум</label>
              <div 
                className="arm-custom-input-display"
                onClick={() => setShowDatePicker(true)}
              >
                {convertDateToShow(date)}
              </div>
            </div>
            <div className="arm-form-group">
              <label htmlFor="time">Време</label>
              <div 
                className="arm-custom-input-display"
                onClick={() => setShowTimePicker(true)}
              >
                {convertTimeToShow(time)}
              </div>
            </div>
            <div className="arm-form-group">
              <label htmlFor="phone">Телефонски број</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onFocus={handleInputFocus}
                placeholder={isWalkIn ? 'Опционално' : '+389 7x xxx xxx'}
              />
            </div>
            <div className="arm-form-group">
              <label htmlFor="additional">Дополнително Барање</label>
              <input
                type="text"
                id="additional"
                value={additionalRequest}
                onChange={(e) => setAdditionalRequest(e.target.value)}
                onFocus={handleInputFocus}
              />
            </div>
            <div className="arm-form-group">
              <label htmlFor="email">Email адреса</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={handleInputFocus}
                placeholder={isWalkIn ? 'Опционално' : 'primer@gmail.com'}
              />
            </div>
            <div className="arm-form-group">
              <label htmlFor="language">Јазик за резервација</label>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="arm-language-select"
              >
                <option value="MK">Македонски</option>
                <option value="EN">English</option>
              </select>
            </div>
            {error && <p className="arm-error-message">{error}</p>}
            <div className="arm-modal-actions">
              <button type="button" onClick={onClose} className="arm-cancel-button">
                Откажи
              </button>
              <button type="submit" className="arm-submit-button" disabled={isSubmitting}>
                {isWalkIn ? 'Додади влез' : 'Додади'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showDatePicker && (
        <CustomDatePicker
          selectedDate={date}
          onDateSelect={setDate}
          onClose={() => setShowDatePicker(false)}
        />
      )}

      {showTimePicker && (
        <CustomTimePicker
          value={time}
          onChange={setTime}
          onClose={() => setShowTimePicker(false)}
        />
      )}
    </>
  );
};

export default AddReservationModal;